// LivestreamDetailOnlineHeader.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import UniversalComponent from '../component/UniversalComponent.jsx';

import {
  liveStreamSessionLeaderboardCategory,
  RANGE_SESSION,
  GIFT_LEADERBOARD_MOUNT_ELEMENT_ID,
} from '../resource/liveStreamConstants.js';
import { ButtonId } from '../resource/mixpanel.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';

import HotLiveIcon from '../../img/ic-hot-live.svg';

export const LivestreamDetailOnlineHeader = ({
  streamId = null,
  viewers = '-',
  needStreamTokenForExclusivePrivatePerforming = false,
  onOpenExploreMore = NULL_FUNCTION,
  isActive = false,
  shouldUseOverlayCta = false,
  isExclusivePrivatePerforming = false,
}) => {
  const { t } = useTranslation();
  const trackingPayload = useMemo(
    () => ({
      'stream.id': streamId,
    }),
    [streamId]
  );

  return (
    <StyledLivestreamDetailOnlineHeader>
      <LiveStreamUserWrapper>
        <UniversalComponent
          filename="Container_LiveStreamUser"
          userId={streamId}
          streamId={streamId}
          importer={
            /* #__LOADABLE__ */ () => import('../container/LiveStreamUser.js')
          }
        />
      </LiveStreamUserWrapper>

      <StatusPanel>
        <StatusLeft>
          <UniversalComponent
            filename="Container_LivestreamDetailStreamLabel"
            streamId={streamId}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LivestreamDetailStreamLabel.js')
            }
          />
        </StatusLeft>
        <StatusRight>
          {!isExclusivePrivatePerforming && (
            <HotLivestreamButton
              data-element_id={ButtonId.LiveStream.ButtonExploreMoreLivestream}
              data-tracking_payload={trackingPayload}
              onClick={onOpenExploreMore}
            >
              <img src={HotLiveIcon} alt="hot-live" />
              <HotLivestreamText>
                {t('button_explore_more', {
                  ns: TranslationNamespace.FEED,
                })}
              </HotLivestreamText>
            </HotLivestreamButton>
          )}

          {!needStreamTokenForExclusivePrivatePerforming &&
            !shouldUseOverlayCta && (
              <ToyCommandContainer>
                <ToyCommandRow>
                  <UniversalComponent
                    filename="Container_ToyCommandAnimationPanel"
                    streamId={streamId}
                    isToy
                    importer={
                      /* #__LOADABLE__ */ () =>
                        import('../container/ToyCommandAnimationPanel.js')
                    }
                  />
                </ToyCommandRow>

                <ToyCommandRow>
                  <UniversalComponent
                    filename="Container_ToyCommandAnimationPanel"
                    streamId={streamId}
                    importer={
                      /* #__LOADABLE__ */ () =>
                        import('../container/ToyCommandAnimationPanel.js')
                    }
                  />
                </ToyCommandRow>
              </ToyCommandContainer>
            )}
        </StatusRight>
      </StatusPanel>
      {needStreamTokenForExclusivePrivatePerforming ? null : (
        <LeaderboardPanel
          data-sheet_mount_id={
            isActive ? GIFT_LEADERBOARD_MOUNT_ELEMENT_ID : undefined
          }
        >
          <UniversalComponent
            filename="Container_LiveStreamTopLeaderboard"
            streamId={streamId}
            range={RANGE_SESSION}
            category={liveStreamSessionLeaderboardCategory.TOTAL}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LiveStreamTopLeaderboard.js')
            }
          />
          <ViewersText>{viewers}</ViewersText>
        </LeaderboardPanel>
      )}
    </StyledLivestreamDetailOnlineHeader>
  );
};

LivestreamDetailOnlineHeader.propTypes = {
  streamId: PropTypes.string,
  viewers: PropTypes.string,
  needStreamTokenForExclusivePrivatePerforming: PropTypes.bool,
  onOpenExploreMore: PropTypes.func,
  isActive: PropTypes.bool,
  shouldUseOverlayCta: PropTypes.bool,
  isExclusivePrivatePerforming: PropTypes.bool,
};

const StyledLivestreamDetailOnlineHeader = styled.header`
  position: relative;
  min-height: 64px;
  width: 100%;
  padding: 12px 8px 8px;
  user-select: none;
  z-index: 2;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  grid-template-areas:
    'user-panel leaderboard'
    'status status';

  ${media.tablet`
    grid-template-columns: auto 44px 1fr;
    grid-template-areas:
      'user-panel hot-live leaderboard'
      'status status status';
  `};
`;

const LiveStreamUserWrapper = styled.div`
  grid-area: user-panel;
  z-index: 1;
  display: flex;
  align-items: center;
`;

const HotLivestreamButton = styled.button`
  ${ResetButtonStyle};
  border-radius: 18px;
  grid-area: hot-live;
  display: none;
  background-color: ${color.neutral['0_alpha_20']};
  > img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }

  ${media.tablet`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 1px 8px;
  `};
`;

const HotLivestreamText = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${color.neutral[100]};
`;

const LeaderboardPanel = styled.div`
  grid-area: leaderboard;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 28px;
  margin-left: 16px;
  margin-right: 40px;
  overflow: hidden;
  ${media.tablet`
    margin-right: 32px;
  `};
  > [class^='UniversalAnchor_'] {
    width: auto !important;
    overflow: hidden;
  }
`;

const StatusPanel = styled.div`
  grid-area: status;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 12px;
  z-index: 1;
`;

const StatusLeft = styled.div`
  display: inline-flex;
  align-items: flex-start;
`;

const StatusRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const ToyCommandContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const ToyCommandRow = styled.div`
  display: inline-flex;
`;

const ViewersText = styled.div`
  margin-left: 2px;
  margin-right: 4px;
  border-radius: 18px;
  padding: 0px 4px;
  flex: none;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  background-color: ${color.neutral['0_alpha_20']};
  color: ${color.neutral[100]};
`;

export default LivestreamDetailOnlineHeader;
